import { Component } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { getString } from '../../locale'
import classNames from 'classnames'

const mimeTypeRegexp = /^(application|audio|example|image|message|model|multipart|text|video)\/[a-z0-9\.\+\*-]+$/
const extRegexp = /\.[a-zA-Z0-9]*$/

export interface MyFile extends File {
    extension: string
    id: string
    preview: { type: string; url?: string }
    sizeReadable: string
}

export enum FilesErrorCode {
    InvalidFileType = 1,
    TooLarge = 2,
    TooSmall = 3,
    MaximumFileCountReached = 4,
}

type Props = {
    multiple?: boolean
    clickable?: boolean
    maxFiles?: number
    maxFileSize?: number
    minFileSize?: number
    dropActiveClassName?: string
    accepts?: string[]
    name?: string
    className?: string
    style?: React.CSSProperties
    onChange: (files: MyFile[]) => void
    onError: (code: FilesErrorCode, file: MyFile) => void
    disabled?: boolean
}

type State = {
    files: MyFile[]
}

export class Files extends Component<Props, State> {
    static propTypes = {
        children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
        className: PropTypes.string.isRequired,
        dropActiveClassName: PropTypes.string,
        onChange: PropTypes.func,
        onError: PropTypes.func,
        accepts: PropTypes.array,
        multiple: PropTypes.bool,
        maxFiles: PropTypes.number,
        maxFileSize: PropTypes.number,
        minFileSize: PropTypes.number,
        clickable: PropTypes.bool,
        name: PropTypes.string,
        style: PropTypes.object,
        disabled: PropTypes.bool,
    }
    static defaultProps = {
        onChange: function (files: MyFile[]) {
            console.log(files)
        },
        onError: function (code: FilesErrorCode, file: MyFile) {
            console.log('files', code, file)
            switch (code) {
                case FilesErrorCode.InvalidFileType:
                    toast.error(getString('files_error.invalid_file_type'))
                    break
                case FilesErrorCode.TooLarge:
                    toast.error(getString('files_error.too_large'))
                    break
                case FilesErrorCode.TooSmall:
                    toast.error(getString('files_error.too_small'))
                    break
                case FilesErrorCode.MaximumFileCountReached:
                    toast.error(getString('files_error.maximum_file_count_reached'))
                    break
            }
        },
        className: 'files-dropzone',
        dropActiveClassName: 'files-dropzone-active',
        accepts: null,
        multiple: true,
        maxFiles: Infinity,
        maxFileSize: Infinity,
        minFileSize: 0,
        name: 'file',
        clickable: true,
        disabled: false,
    }

    state: State
    id: number
    inputElement: HTMLInputElement | null = null
    dropzone: HTMLDivElement | null = null

    constructor(props: Props) {
        super(props)
        this.onDrop = this.onDrop.bind(this)
        this.onDragEnter = this.onDragEnter.bind(this)
        this.onDragLeave = this.onDragLeave.bind(this)
        this.openFileChooser = this.openFileChooser.bind(this)

        this.id = 1

        this.state = {
            files: [],
        }
    }

    onDrop(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault()
        if (this.props.disabled) return

        this.onDragLeave(event)

        // Collect added files, perform checking, cast pseudo-array to Array,
        // then return to method
        let filesAdded = (event.dataTransfer ? event.dataTransfer.files : (event.target as HTMLInputElement).files) as unknown as MyFile[]

        // Multiple files dropped when not allowed
        if (this.props.multiple === false && filesAdded.length > 1) {
            filesAdded = [filesAdded[0]]
        }

        let files = []
        for (let i = 0; i < filesAdded.length; i++) {
            let file = filesAdded[i]

            // Assign file an id
            file.id = 'files-' + this.id++

            // Tell file it's own extension
            file.extension = this.fileExtension(file)

            // Tell file it's own readable size
            file.sizeReadable = this.fileSizeReadable(file.size)

            // Add preview, either image or file extension
            if (file.type && this.mimeTypeLeft(file.type) === 'image') {
                file.preview = {
                    type: 'image',
                    url: window.URL.createObjectURL(file),
                }
            } else {
                file.preview = {
                    type: 'file',
                }
            }

            // Check for file max limit
            if (this.state.files.length + files.length >= this.props.maxFiles!) {
                this.onError(FilesErrorCode.MaximumFileCountReached, file)
                break
            }

            // If file is acceptable, push or replace
            if (this.fileTypeAcceptable(file) && this.fileSizeAcceptable(file)) {
                files.push(file)
            }
        }
        this.setState(
            {
                files: (this.props.multiple === false ? files : [...this.state.files, ...files]) as MyFile[],
            },
            () => {
                this.props.onChange.call(this, this.state.files)
            },
        )
    }

    onDragOver(event: React.DragEvent<HTMLDivElement>) {
        event.preventDefault()
        event.stopPropagation()
    }

    onDragEnter(event: React.DragEvent<HTMLDivElement>) {
        if (this.props.disabled) return
        if (this.dropzone) {
            let el = this.dropzone
            el.className += ' ' + this.props.dropActiveClassName!
        }
    }

    onDragLeave(event: React.DragEvent<HTMLDivElement>) {
        if (this.props.disabled) return
        if (this.dropzone) {
            let el = this.dropzone
            this.dropzone.className = el.className.replace(' ' + this.props.dropActiveClassName, '')
        }
    }

    openFileChooser() {
        if (this.props.disabled) return
        if (this.inputElement) {
            this.inputElement.value = ''
            this.inputElement.click()
        }
    }

    fileTypeAcceptable(file: MyFile) {
        let accepts = this.props.accepts
        if (!accepts) {
            return true
        }

        const result = accepts.some(accept => {
            if (file.type && accept.match(mimeTypeRegexp)) {
                let typeLeft = this.mimeTypeLeft(file.type)
                let typeRight = this.mimeTypeRight(file.type)
                let acceptLeft = accept.split('/')[0]
                let acceptRight = accept.split('/')[1]
                if (acceptLeft && acceptRight) {
                    if (acceptLeft === typeLeft && acceptRight === '*') {
                        return true
                    }
                    if (acceptLeft === typeLeft && acceptRight === typeRight) {
                        return true
                    }
                }
            } else if (file.extension && accept.match(extRegexp)) {
                const ext = accept.substr(1)
                return file.extension.toLowerCase() === ext.toLowerCase()
            }
            return false
        })

        if (!result) {
            this.onError(FilesErrorCode.InvalidFileType, file)
        }

        return result
    }

    fileSizeAcceptable(file: MyFile): boolean {
        if (file.size > this.props.maxFileSize!) {
            this.onError(FilesErrorCode.TooLarge, file)
            return false
        } else if (file.size < this.props.minFileSize!) {
            this.onError(FilesErrorCode.TooSmall, file)
            return false
        } else {
            return true
        }
    }

    mimeTypeLeft(mime: string): string {
        return mime.split('/')[0]
    }

    mimeTypeRight(mime: string): string {
        return mime.split('/')[1]
    }

    fileExtension(file: File): string {
        let extensionSplit = file.name.split('.')
        if (extensionSplit.length > 1) {
            return extensionSplit[extensionSplit.length - 1]
        } else {
            return 'none'
        }
    }

    fileSizeReadable(size: number): string {
        if (size >= 1000000000) {
            return Math.ceil((size / 1000000000) * 100) / 100 + 'GB'
        } else if (size >= 1000000) {
            return Math.ceil((size / 1000000) * 100) / 100 + 'MB'
        } else if (size >= 1000) {
            return Math.ceil((size / 1000) * 100) / 100 + 'kB'
        } else {
            return Math.ceil(size) + 'B'
        }
    }

    onError(code: FilesErrorCode, file: MyFile) {
        this.props.onError.call(this, code, file)
    }

    removeFile(fileToRemove: File[]) {
        this.setState(
            {
                // @ts-ignore
                files: this.state.files.filter(file => file.id !== fileToRemove.id),
            },
            () => {
                this.props.onChange.call(this, this.state.files)
            },
        )
    }

    removeFiles() {
        this.setState(
            {
                files: [],
            },
            () => {
                this.props.onChange.call(this, this.state.files)
            },
        )
    }

    render() {
        return (
            <div>
                <input
                    type="file"
                    accept={this.props.accepts ? this.props.accepts.join() : ''}
                    multiple={this.props.multiple}
                    name={this.props.name}
                    style={{ display: 'none' }}
                    ref={ref => (this.inputElement = ref)}
                    onChange={this.onDrop as any}
                />
                <div
                    className={classNames(this.props.className, this.props.disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer')}
                    onClick={(this.props.clickable === true ? this.openFileChooser : null) as any}
                    onDrop={this.onDrop}
                    onDragOver={this.onDragOver}
                    onDragEnter={this.onDragEnter}
                    onDragLeave={this.onDragLeave}
                    ref={ref => (this.dropzone = ref)}
                    style={this.props.style}
                >
                    {this.props.children}
                </div>
            </div>
        )
    }
}
